/* eslint-disable indent */
import { Box, Divider, Skeleton } from "@mui/material";

import Colors from "@constants/Colors";
import {
  ErrorState,
  formatNumberWithComma,
  formatNumberWithCommaAndDollar,
  formatWithPercentage,
} from "@src/constants";
import CustomChart from "@src/components/shared-ui-components/CustomChart";
import {
  Legend,
  LegendBox,
  StyledRootBox,
  StyledSalesBox,
  StyledSalesRow,
  StyledTitle,
  StyledTypographySales,
  StyledValue,
  StyledValueBox,
} from "./style";
import { HRChartProps, NetSalesProps } from "./type";

const InfoBox = ({
  title,
  sales,
  percentage,
  backgroundColor,
  textColor,
  font,
  loading,
  hideDollar,
  isFloat,
}: NetSalesProps) => {
  let content: React.ReactNode = (
    <StyledValueBox>
      {percentage && (
        <>
          {!percentage ? (
            <ErrorState />
          ) : (
            <StyledValue sx={{ minWidth: 0 }}>
              {formatWithPercentage(percentage || "", 0, true)}
            </StyledValue>
          )}
          <Divider orientation="vertical" />
        </>
      )}
      <StyledValue>
        {hideDollar
          ? formatNumberWithComma(sales || "", isFloat ? 2 : 0, !isFloat)
          : formatNumberWithCommaAndDollar(
              sales || "",
              isFloat ? 2 : 0,
              !isFloat
            )}
      </StyledValue>
    </StyledValueBox>
  );

  if (!sales) content = <ErrorState />;
  if (loading) content = <Skeleton width="70%" />;
  return (
    <StyledSalesBox>
      <StyledTypographySales
        background={backgroundColor || ""}
        sx={{
          color: textColor || Colors.neutral_gray_500,
          backgroundColor: backgroundColor || "",
          fontFamily: font || "Ringside Regular-Book !important",
        }}
      >
        {title}
      </StyledTypographySales>
      {content}
    </StyledSalesBox>
  );
};

const HRChart = ({
  firstDataset,
  chartType,
  labels,
  firstColor,
  secondColor,
  secondDataSet,
  title,
  rowData,
  annotationColor,
  loading,
}: HRChartProps) => {
  const formattedLables = labels?.map((str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  });

  return (
    <StyledRootBox>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <StyledTitle color={Colors?.primary}>{title}</StyledTitle>
        <div
          style={{
            display: ["COGS", "Labour"].includes(title) ? "block" : "none",
            height: 0,
          }}
        >
          <LegendBox>
            <Legend backgroundColor={firstColor} />
            <StyledTypographySales background="#FFF" sx={{ gap: 1 }}>
              Actual {title}
            </StyledTypographySales>
          </LegendBox>

          <LegendBox>
            <Legend backgroundColor={secondColor} />
            <StyledTypographySales background="#FFF" sx={{ gap: 1 }}>
              Plan {title}
            </StyledTypographySales>
          </LegendBox>
        </div>
      </Box>
      <Box
        sx={{
          minHeight: "204px !important",
          display: "flex",
          alignItems: "center",
        }}
      >
        <CustomChart
          loading={loading}
          labels={formattedLables}
          firstDataset={firstDataset}
          secondDataset={secondDataSet}
          targetPlanValue={rowData?.targetPlanValue}
          targetTotalValue={rowData?.targetTotalValue}
          maxTickLimit={chartType === "line" ? 6 : 12}
          type={chartType}
          firstColor={firstColor}
          secondColor={secondColor}
          annotationColor={annotationColor}
          hideDollar={rowData?.total?.hideDollar || rowData?.total?.hideDollar}
          showPercentage={!!rowData?.total?.pct || !!rowData?.plan?.pct}
          showEmptyState={!rowData?.total?.amount && !rowData?.plan?.amount}
        />
      </Box>
      <StyledSalesRow>
        <InfoBox
          title={rowData?.total?.title}
          sales={rowData?.total?.amount}
          textColor={rowData?.total?.textColor}
          backgroundColor={rowData?.total?.backgroundColor}
          font={rowData?.total?.font}
          loading={loading}
          percentage={rowData?.total?.pct}
          hideDollar={rowData?.total?.hideDollar}
          isFloat={rowData?.total?.isFloat}
        />
        <InfoBox
          title={rowData?.plan?.title}
          sales={rowData?.plan?.amount}
          textColor={rowData?.plan?.textColor}
          backgroundColor={rowData?.plan?.backgroundColor}
          font={rowData?.plan?.font}
          loading={loading}
          percentage={rowData?.plan?.pct}
          hideDollar={rowData?.plan?.hideDollar}
          isFloat={rowData?.plan?.isFloat}
        />
      </StyledSalesRow>
      {rowData?.netPerCap?.title && rowData?.grossPerCap?.title && (
        <>
          <Divider />
          <StyledSalesRow>
            <InfoBox
              title={rowData?.netPerCap?.title}
              sales={rowData?.netPerCap?.amount}
              loading={loading}
              isFloat={rowData?.netPerCap?.isFloat}
            />
            <InfoBox
              title={rowData?.grossPerCap?.title}
              sales={rowData?.grossPerCap?.amount}
              loading={loading}
              isFloat={rowData?.grossPerCap?.isFloat}
            />
          </StyledSalesRow>
        </>
      )}
    </StyledRootBox>
  );
};

export default HRChart;
